import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Bell, CalendarCheck, House, LayoutGrid, PenLine, Sparkles, UserPlus, XCircle } from 'lucide-react'
import { lazy, Suspense } from 'react'
import { Link, useLocation, useSearch } from 'wouter'
import { ChangesetList } from '../components/ChangesetList'
import { KpiCard, KpiDrawer } from '../components/Kpi'
import { useAgent, useWorkspace } from '../components/Shell'
import { Card, CardHead, ErrorBox, Loading, PageHead } from '../components/ui'
import { api } from '../lib/api'
import { ago } from '../lib/format'
import { keys, useHome } from '../lib/hooks'
import type { Envelope, HomeCycle } from '../lib/types'

const PageView = lazy(() => import('./PageView').then(m => ({ default: m.PageView })))

/** Home: the standard one, or the page someone chose as their Home (with the standard one a click away). */
export function Home() {
  const { wid } = useWorkspace()
  const standard = new URLSearchParams(useSearch()).has('standard')
  const pages = useQuery({
    queryKey: ['ws', wid, 'pages'],
    queryFn: () => api.get<{ pages: { id: string; title: string }[]; home: string | null }>(`/w/${wid}/pages`),
  })
  const mine = pages.data?.home && pages.data.pages.some(p => p.id === pages.data?.home) ? pages.data.home : null
  if (pages.isLoading)
    return (
      <div className="page">
        <Loading rows={6} />
      </div>
    )
  if (mine && !standard)
    return (
      <>
        <div className="home-page-bar">
          <House size={13} />
          <span className="grow">This page is your Home.</span>
          <Link href="/?standard=1" className="link-btn">
            Standard Home
          </Link>
          <Link href={`/pages/${mine}`} className="link-btn">
            Open as page
          </Link>
        </div>
        <Suspense
          fallback={
            <div className="page">
              <Loading rows={6} />
            </div>
          }
        >
          <PageView id={mine} embedded />
        </Suspense>
      </>
    )
  return <StandardHome />
}

function StandardHome() {
  const { wid, state, me } = useWorkspace()
  const home = useHome(wid)
  const { ask } = useAgent()
  const qc = useQueryClient()
  // the open KPI lives in the URL, so a drill-down can be linked and survives a refresh
  const search = new URLSearchParams(useSearch())
  const [, navigate] = useLocation()
  const kpiOpen = search.get('kpi')
  const openKpi = (key: string | null) => navigate(key ? `/?kpi=${encodeURIComponent(key)}` : '/', { replace: !key })
  if (home.isLoading)
    return (
      <div className="page">
        <Loading rows={6} />
      </div>
    )
  if (home.error)
    return (
      <div className="page">
        <ErrorBox error={home.error} />
      </div>
    )
  const h = home.data
  if (!h || !state) return null
  const first = me?.user.name.split(' ')[0] ?? ''
  const reviewer = state.role !== 'contributor' && state.role !== 'viewer'
  const failing = h.tests.failing
  const reviews = reviewer ? h.reviews : []
  // a notification about a change already listed here would only say it twice
  const shown = new Set([...reviews, ...h.myOwned, ...h.mine].map(c => `/changesets/${c.id}`))
  const unread = h.notifications.filter(n => !n.readAt && !(n.link && shown.has(n.link)))
  // Home shows what needs you; a section with nothing in it isn't drawn
  const side = h.lastClose || unread.length > 0
  const any = reviews.length || h.myOwned.length || h.mine.length || h.recent.length || failing.length || side
  const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`

  return (
    <div className="page">
      <PageHead
        title={`Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, ${first}`}
        sub={
          h.owns.length
            ? `You plan ${h.owns.map(o => o.label).join(', ')}.`
            : reviewer
              ? reviews.length
                ? `${plural(reviews.length, 'change')} waiting for your review.`
                : 'Nothing is waiting for your review.'
              : undefined
        }
        right={
          <>
            {h.owns.length > 0 ? (
              <Link href="/plan" className="btn">
                <PenLine size={14} /> Open my plan
              </Link>
            ) : (
              <Link href="/views" className="btn">
                <LayoutGrid size={14} /> Open a view
              </Link>
            )}
            <button className="btn primary" onClick={() => ask('Summarize where the plan stands and what changed recently.')}>
              <Sparkles size={14} /> Brief me
            </button>
          </>
        }
      />

      {h.cycle && <CycleBanner c={h.cycle} env={h.envelope} reviewer={reviewer} />}
      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        {h.kpis.map(k => (
          <KpiCard key={k.key} k={k} onOpen={() => openKpi(k.key)} />
        ))}
      </div>
      {kpiOpen && <KpiDrawer kpiKey={kpiOpen} label={h.kpis.find(k => k.key === kpiOpen)?.label ?? ''} onClose={() => openKpi(null)} />}

      {!any ? (
        <div className="home-quiet">You’re all caught up.</div>
      ) : (
        <div
          className="grid split-md"
          style={{ gridTemplateColumns: side ? 'minmax(0, 2fr) minmax(280px, 1fr)' : 'minmax(0, 1fr)', gap: 20 }}
        >
          <div className="stack">
            {failing.length > 0 && (
              <Card>
                <CardHead
                  title={`${plural(failing.length, 'model test')} failing`}
                  icon={<XCircle size={15} color="var(--red)" style={{ marginRight: 6 }} />}
                  sub="Changes can’t be approved into the plan while a blocking test fails."
                  right={
                    <Link href="/model?tab=tests" className="btn ghost sm">
                      Open tests
                    </Link>
                  }
                />
                <table className="t">
                  <tbody>
                    {failing.map(t => (
                      <tr key={t.name}>
                        <td>
                          <div className="small strong">{t.description || t.name}</div>
                        </td>
                        <td className="r">
                          <span className={`pill ${t.severity === 'blocking' ? 'red' : 'amber'}`}>{t.failingCount} failing</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
            {reviews.length > 0 && (
              <Card>
                <CardHead
                  title="Waiting for your review"
                  right={
                    <Link href="/reviews" className="btn ghost sm">
                      All reviews
                    </Link>
                  }
                />
                <ChangesetList items={reviews} />
              </Card>
            )}
            {h.myOwned.length > 0 && (
              <Card>
                <CardHead title="Touching what you own" />
                <ChangesetList items={h.myOwned} />
              </Card>
            )}
            {h.mine.length > 0 && (
              <Card>
                <CardHead title="Your changes" sub="Drafts and submissions, including what the agent did for you." />
                <ChangesetList items={h.mine} />
              </Card>
            )}
            {h.recent.length > 0 && (
              <Card>
                <CardHead title="Recently added to the plan" />
                <ChangesetList items={h.recent.slice(0, 5)} />
              </Card>
            )}
          </div>
          {side && (
            <div className="stack" style={{ alignContent: 'start' }}>
              {unread.length > 0 && (
                <Card>
                  <CardHead
                    title="Notifications"
                    icon={<Bell size={15} style={{ marginRight: 6 }} />}
                    right={
                      <button
                        className="btn ghost sm"
                        onClick={async () => {
                          await api.post(`/w/${wid}/notifications/read`)
                          await qc.invalidateQueries({ queryKey: keys.home(wid) })
                          await qc.invalidateQueries({ queryKey: keys.state(wid) })
                        }}
                      >
                        Mark read
                      </button>
                    }
                  />
                  <div style={{ display: 'grid' }}>
                    {unread.slice(0, 5).map(n => {
                      const body = (
                        <div key={n.id} style={{ padding: '10px 20px', borderTop: '1px solid var(--border)' }}>
                          <div className="small strong">{n.title}</div>
                          {n.body && <div className="small muted">{n.body}</div>}
                          <div className="tiny faint">{ago(n.createdAt)}</div>
                        </div>
                      )
                      return n.link ? (
                        <Link key={n.id} href={n.link} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {body}
                        </Link>
                      ) : (
                        <div key={n.id}>{body}</div>
                      )
                    })}
                  </div>
                </Card>
              )}
              {h.lastClose && (
                <Card>
                  <CardHead
                    title="Last close"
                    icon={<CalendarCheck size={15} style={{ marginRight: 6 }} />}
                    sub={`${h.lastClose.period} closed ${ago(h.lastClose.at)}`}
                    right={
                      <Link href={`/changesets/${h.lastClose.calendar}`} className="btn ghost sm">
                        Details
                      </Link>
                    }
                  />
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const OWNER_STATUS: Record<string, { label: string; tone: string; next: string }> = {
  'not started': { label: 'Not started', tone: '', next: 'Start planning' },
  drafting: { label: 'Drafting', tone: 'amber', next: 'Continue' },
  submitted: { label: 'Submitted', tone: 'blue', next: 'View' },
  approved: { label: 'Approved', tone: 'green', next: 'View' },
  returned: { label: 'Sent back', tone: 'red', next: 'Revise' },
}

/** What to do next in the live cycle: an owner's own submission, or FP&A's progress and envelope. */
function CycleBanner({ c, env, reviewer }: { c: HomeCycle; env: Envelope | null; reviewer: boolean }) {
  const over = env?.rows.filter(r => r.over) ?? []
  // a monthly close isn't a submission round: say where it stands and leave the owners' table out
  if (c.kind === 'close')
    return reviewer ? (
      <div className="cycle-banner">
        <div className="cycle-banner-head">
          <span className="eyebrow">{c.name}</span>
          <div className="grow" />
          <Link href="/cycle" className="link-btn strong">
            Open the cycle <ArrowRight size={12} />
          </Link>
        </div>
        <div className="cycle-banner-row">
          <CalendarCheck size={15} />
          <span className="grow">{c.detail}</span>
        </div>
      </div>
    ) : null
  if (!c.mine.length && !reviewer) return null
  return (
    <div className="cycle-banner">
      <div className="cycle-banner-head">
        <span className="eyebrow">{c.name}</span>
        {c.due && (
          <span className={`small ${c.overdue ? 'x-bad' : 'faint'}`}>
            {c.overdue ? 'overdue since' : 'due'} {longDate(c.due)}
          </span>
        )}
        <div className="grow" />
        {reviewer && (
          <Link href="/cycle" className="link-btn strong">
            Open the cycle <ArrowRight size={12} />
          </Link>
        )}
      </div>
      {c.mine.map(m => {
        const st = OWNER_STATUS[m.status] ?? OWNER_STATUS['not started']
        const act = m.status === 'not started' || m.status === 'drafting' || m.status === 'returned'
        return (
          <div key={m.id} className="cycle-banner-row">
            <PenLine size={15} />
            <span className="grow">
              Plan <b>{m.label}</b> for {c.name.replace(/ Budget$/, '')}
              <span className={`pill ${st?.tone ?? ''}`} style={{ marginLeft: 8 }}>
                {st?.label}
              </span>
            </span>
            <Link href={`/plan?member=${encodeURIComponent(m.id)}`} className={`btn sm ${act ? 'primary' : ''}`}>
              {st?.next} <ArrowRight size={12} />
            </Link>
          </div>
        )
      })}
      {reviewer && c.unassigned > 0 && (
        <div className="cycle-banner-row">
          <UserPlus size={15} />
          <span className="grow">
            <b>
              {c.unassigned} {c.level?.replace(/_/g, ' ') ?? 'owner'}
              {c.unassigned > 1 ? 's have' : ' has'}
            </b>{' '}
            nobody to plan {c.unassigned > 1 ? 'them' : 'it'} yet. Invite each owner; the link takes them straight to their plan.
          </span>
          <Link href="/settings?tab=members" className="btn sm primary">
            Invite owners <ArrowRight size={12} />
          </Link>
        </div>
      )}
      {reviewer && (
        <div className="cycle-banner-row">
          <CalendarCheck size={15} />
          <span className="grow">
            <b>
              {c.submitted} of {c.owners}
            </b>{' '}
            submitted
            {env && c.kind === 'budget' && (
              <span className="faint">
                {' · '}
                {over.length
                  ? `${over.map(r => r.label).join(', ')} over the ${env.label}`
                  : `every ${env.levelLabel} inside the ${env.label}`}
              </span>
            )}
          </span>
          <div className="cycle-progress">
            <span style={{ width: `${c.owners ? (c.submitted / c.owners) * 100 : 0}%` }} />
          </div>
        </div>
      )}
    </div>
  )
}

const longDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
